import { useCommittedRef } from "@sg-protect/components";
import { useEventBusListener } from "@sg-protect/federation-runtime";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { BULK_OPERATION_STARTED_EVENT, type BulkOperationStartedPayload } from "../../events.ts";

const COACHMARK_DURATION_MS = 5000;
// Outlasts the 30 s idle poll of the bulk operations, in case the refetch on start misses the new bulk.
const PENDING_FEEDBACK_MS = 35_000;

export function useActionStartedFeedback(
    triggerRef: RefObject<HTMLButtonElement | null>,
    pulseClassName: string,
    activityCount: number,
    activityBulkIds: string[],
    coachmarkDurationMs = COACHMARK_DURATION_MS
) {
    const [coachmarkRequestedAt, setCoachmarkRequestedAt] = useState<number | null>(null);
    const [isCoachmarkOpen, setIsCoachmarkOpen] = useState(false);
    const [isCoachmarkHovered, setIsCoachmarkHovered] = useState(false);
    const [startCount, setStartCount] = useState(0);
    const pulseRequestedAtRef = useRef<number | null>(null);
    const burstBulkIdsRef = useRef<Set<string> | null>(null);
    const activityCountRef = useCommittedRef(activityCount);
    const activityBulkIdsRef = useCommittedRef(activityBulkIds);

    const handleBulkOperationStarted = useCallback(
        ({ bulkId }: BulkOperationStartedPayload) => {
            const now = Date.now();
            pulseRequestedAtRef.current = now;
            setStartCount(count => count + 1);

            // One start dispatches an event per bulk, and the refetch it triggers may already have counted them.
            if (!burstBulkIdsRef.current) {
                burstBulkIdsRef.current = new Set();
                queueMicrotask(() => {
                    burstBulkIdsRef.current = null;
                });
            }
            const burstBulkIds = burstBulkIdsRef.current;
            burstBulkIds.add(bulkId);

            const countedBurstCount = (activityBulkIdsRef.current ?? []).filter(id => burstBulkIds.has(id)).length;

            if ((activityCountRef.current ?? 0) - countedBurstCount === 0) {
                setCoachmarkRequestedAt(previous => previous ?? now);
            }
        },
        [activityCountRef, activityBulkIdsRef]
    );

    useEventBusListener(BULK_OPERATION_STARTED_EVENT, handleBulkOperationStarted);

    const isPillRendered = activityCount > 0;

    // The event usually lands before the refetched operations render the pill, so the feedback waits for it.
    useEffect(() => {
        if (coachmarkRequestedAt === null || !isPillRendered) {
            return;
        }

        setCoachmarkRequestedAt(null);

        if (Date.now() - coachmarkRequestedAt <= PENDING_FEEDBACK_MS) {
            setIsCoachmarkOpen(true);
            setIsCoachmarkHovered(false);
        }
    }, [coachmarkRequestedAt, isPillRendered]);

    // The popover unmounts with the pill without firing mouseleave, so drop its state there.
    useEffect(() => {
        if (!isPillRendered) {
            setIsCoachmarkOpen(false);
            setIsCoachmarkHovered(false);
        }
    }, [isPillRendered]);

    useEffect(() => {
        if (!isCoachmarkOpen || isCoachmarkHovered) {
            return;
        }

        const timeoutId = setTimeout(() => setIsCoachmarkOpen(false), coachmarkDurationMs);

        return () => clearTimeout(timeoutId);
    }, [isCoachmarkOpen, isCoachmarkHovered, coachmarkDurationMs]);

    useEffect(() => {
        const node = triggerRef.current;
        const requestedAt = pulseRequestedAtRef.current;

        if (!node || requestedAt === null) {
            return;
        }

        pulseRequestedAtRef.current = null;

        if (Date.now() - requestedAt > PENDING_FEEDBACK_MS) {
            return;
        }

        // Removing the class and forcing a reflow before adding it back restarts a pulse already running.
        node.classList.remove(pulseClassName);
        void node.offsetWidth;
        node.classList.add(pulseClassName);

        const handleAnimationEnd = (event: AnimationEvent) => {
            if (event.target === node) {
                node.classList.remove(pulseClassName);
            }
        };
        node.addEventListener("animationend", handleAnimationEnd);

        return () => node.removeEventListener("animationend", handleAnimationEnd);
    }, [triggerRef, pulseClassName, startCount, isPillRendered]);

    const closeCoachmark = useCallback(() => {
        setIsCoachmarkOpen(false);
        setIsCoachmarkHovered(false);
    }, []);

    return { isCoachmarkOpen, setIsCoachmarkHovered, closeCoachmark, startCount };
}
